import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import useYupValidationResolver from '@/lib/hooks/yup';
import { useContentTracking } from '@/lib/hooks/tracking';
import { ModalComponentBaseProps } from '@pcmp/react-providers';
import { Sheets, ModalHeader, ModalFooter, ModalBody } from '@ingka/modal';
import Button from '@ingka/button';
import {
  createStoragePointSchema,
  CreateStoragePointFormData,
} from '@/lib/schemas/create-storage-point';
import ControlledInput from '@/components/forms/controlled-input';

type NewStoragePointSiderProps = ModalComponentBaseProps;

export const NewStoragePointSider: FC<
  NewStoragePointSiderProps & {
    data: CreateStoragePointFormData;
    onCreate: (data: CreateStoragePointFormData) => Promise<boolean>;
  }
> = ({ handleDismiss, onCreate, data, ...rest }) => {
  // Hooks
  const { t } = useTranslation();
  const trackContent = useContentTracking();

  const isEditMode = !!data.warehouseId;
  const titleKey = isEditMode
    ? 'page.admin.items.editStoragePoint.title'
    : 'page.admin.items.newStoragePoint.title';
  const descriptionKey = isEditMode
    ? 'page.admin.items.editStoragePoint.description'
    : 'page.admin.items.newStoragePoint.description';

  const resolver = useYupValidationResolver(createStoragePointSchema);
  const form = useForm({
    mode: 'all',
    resolver,
    defaultValues: {
      name: data.name,
      locationId: data.locationId || '',
      warehouseId: data.warehouseId || '',
    },
    criteriaMode: 'all',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDisabled = !form.formState.isValid || isSubmitting;

  const submitForm = async () => {
    if (isDisabled) return;
    setIsSubmitting(true);
    try {
      const formData = form.getValues();
      if (await onCreate(formData)) handleDismiss();
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    trackContent(isEditMode ? 'edit-storage-point' : 'new-storage-point', {
      type: 'sider',
    });
  }, [trackContent, isEditMode]);

  return (
    <Sheets
      {...rest}
      aria-label={t(titleKey)}
      header={
        <ModalHeader ariaCloseTxt={t('aria.close')} title={t(titleKey)} />
      }
      footer={
        <ModalFooter>
          <Button
            text={t('label.cancel')}
            type="secondary"
            onClick={handleDismiss}
          />

          <Button
            disabled={isDisabled}
            aria-label={t('label.continue')}
            text={t('label.continue')}
            type="primary"
            onClick={submitForm}
          />
        </ModalFooter>
      }
    >
      <ModalBody>
        <FormProvider {...form}>
          <p className="mb-4">{t(descriptionKey)}</p>

          <ControlledInput<CreateStoragePointFormData>
            name="name"
            label={t('label.name')}
            type="text"
          />
        </FormProvider>
      </ModalBody>
    </Sheets>
  );
};
